// src/Components/Admin/GastosData/GastosData.jsx
// Gastos generales del negocio (no ligados a una venta puntual).
import React, { useEffect, useMemo, useState } from 'react';
import './GastosData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync, faTrash, faPlus, faTimes, faFilter, faSearch,
    faReceipt, faMoneyBillWave, faTags
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import moneda from '../../moneda';
import ventasService from '../../../services/ventasService';

// Categorías sugeridas para clasificar el gasto.
const CATEGORIAS = ['General', 'Arriendo', 'Servicios', 'Insumos', 'Nómina', 'Transporte', 'Publicidad', 'Impuestos', 'Otros'];

const hoyISO = () => {
    const d = new Date();
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
};

export default function GastosData() {
    const [gastos, setGastos] = useState([]);
    const [estadisticas, setEstadisticas] = useState({});
    const [loading, setLoading] = useState(false);

    // Filtros
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [filtroDesde, setFiltroDesde] = useState('');
    const [filtroHasta, setFiltroHasta] = useState('');
    const [busqueda, setBusqueda] = useState('');

    // Formulario de nuevo gasto
    const [formVisible, setFormVisible] = useState(false);
    const [concepto, setConcepto] = useState('');
    const [monto, setMonto] = useState('');
    const [categoria, setCategoria] = useState('General');
    const [metodo, setMetodo] = useState('Efectivo');
    const [fecha, setFecha] = useState(hoyISO());
    const [nota, setNota] = useState('');
    const [guardando, setGuardando] = useState(false);

    // Toasts
    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3600);
    };

    const formatMoney = (v) =>
        `${moneda} ${Number(v || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    useEffect(() => {
        cargarGastos();
    }, [filtroCategoria, filtroDesde, filtroHasta]); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarGastos = async () => {
        setLoading(true);
        const data = await ventasService.listarGastos({
            categoria: filtroCategoria,
            desde: filtroDesde,
            hasta: filtroHasta,
            limite: 500,
        });
        setLoading(false);
        if (data.error) {
            showToast(data.error, 'error');
            setGastos([]);
            setEstadisticas({});
        } else {
            setGastos(data.gastos || []);
            setEstadisticas(data.estadisticas || {});
        }
    };

    // Búsqueda local (inmune a los filtros del servidor).
    const filtrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return gastos;
        return gastos.filter(g =>
            (g.concepto || '').toLowerCase().includes(q) ||
            (g.categoria || '').toLowerCase().includes(q) ||
            (g.nota || '').toLowerCase().includes(q)
        );
    }, [gastos, busqueda]);

    // Total visible (respeta la búsqueda local).
    const totalVisible = useMemo(
        () => filtrados.reduce((acc, g) => acc + Number(g.monto || 0), 0),
        [filtrados]
    );

    const limpiarFiltros = () => {
        setFiltroCategoria('');
        setFiltroDesde('');
        setFiltroHasta('');
        setBusqueda('');
    };

    const resetForm = () => {
        setConcepto('');
        setMonto('');
        setCategoria('General');
        setMetodo('Efectivo');
        setFecha(hoyISO());
        setNota('');
    };

    const guardarGasto = async (e) => {
        e.preventDefault();
        const montoNum = parseFloat(monto);
        if (!concepto.trim()) {
            showToast('El concepto es obligatorio', 'warning');
            return;
        }
        if (Number.isNaN(montoNum) || montoNum <= 0) {
            showToast('El monto debe ser mayor a 0', 'warning');
            return;
        }
        setGuardando(true);
        const res = await ventasService.crearGasto({
            concepto: concepto.trim(),
            monto: montoNum,
            categoria,
            metodo,
            fecha,
            nota: nota.trim(),
        });
        setGuardando(false);
        if (res.error) {
            showToast(res.error, 'error');
            return;
        }
        showToast('Gasto registrado', 'success');
        resetForm();
        setFormVisible(false);
        cargarGastos();
    };

    const eliminarGasto = async (g) => {
        const r = await Swal.fire({
            title: '¿Eliminar gasto?',
            html: `<strong>${g.concepto}</strong><br/>${formatMoney(g.monto)}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc2626',
        });
        if (!r.isConfirmed) return;
        const res = await ventasService.eliminarGasto(g.idGasto);
        if (res.error) {
            showToast(res.error, 'error');
            return;
        }
        showToast('Gasto eliminado', 'success');
        cargarGastos();
    };

    const porCategoria = estadisticas.por_categoria || [];

    return (
        <div>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className="product-toast-container">
                    {toasts.map(t => (
                        <div key={t.id} className={`product-toast ${t.type}`}>
                            <div className="toast-icon">
                                {t.type === 'success' && '✅'}
                                {t.type === 'error' && '❌'}
                                {t.type === 'warning' && '⚠️'}
                                {t.type === 'info' && 'ℹ️'}
                            </div>
                            <div className="toast-content">
                                <div className="toast-title">{t.message}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filtros */}
            <div className="ventas-filtros-bar">
                <div className="vf-buscar">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder="Buscar gasto (concepto, categoría o nota)"
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                    />
                    {busqueda && (
                        <button type="button" className="vf-clear-x" onClick={() => setBusqueda('')} title="Limpiar búsqueda">
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>

                <select className="vf-select" value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} title="Categoría">
                    <option value="">Categoría: todas</option>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                <div className="vf-fechas">
                    <input type="date" value={filtroDesde} onChange={e => setFiltroDesde(e.target.value)} title="Desde" />
                    <span className="vf-fechas-sep">→</span>
                    <input type="date" value={filtroHasta} onChange={e => setFiltroHasta(e.target.value)} title="Hasta" />
                </div>

                <button className="vf-limpiar" onClick={limpiarFiltros} title="Limpiar filtros">
                    <FontAwesomeIcon icon={faFilter} /> Limpiar
                </button>
            </div>

            {/* Header + stats */}
            <div className="global-component-header" style={{ padding: '12px 16px' }}>
                <div className="flex-between" style={{ width: '100%' }}>
                    <div className="flex-center" style={{ gap: '16px', flexWrap: 'wrap' }}>
                        <div className="global-component-title">
                            <FontAwesomeIcon icon={faReceipt} />
                            Gastos
                        </div>
                        <div className="global-component-stats ventas-stats">
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{estadisticas.total_registros ?? filtrados.length}</span>
                                <span className="global-stat-label">Gastos</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney(estadisticas.total_gastos ?? totalVisible)}</span>
                                <span className="global-stat-label">Total gastado</span>
                            </div>
                            {porCategoria.slice(0, 3).map(c => (
                                <div className="global-stat-card compact" key={c.categoria}>
                                    <span className="global-stat-number">{formatMoney(c.total)}</span>
                                    <span className="global-stat-label">{c.categoria}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex-center" style={{ gap: 8 }}>
                        <button className="btn-product-secondary compact flex-center" onClick={cargarGastos} title="Actualizar">
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Botón / formulario nuevo gasto */}
            <div className="new-product-container">
                {!formVisible ? (
                    <button className="btn-product-primary flex-center" onClick={() => setFormVisible(true)}>
                        <FontAwesomeIcon icon={faPlus} /> Registrar gasto
                    </button>
                ) : (
                    <div className="product-section-card gasto-form-card">
                        <div className="product-section-header">
                            <div className="product-section-title">
                                <FontAwesomeIcon icon={faMoneyBillWave} /> Nuevo gasto
                            </div>
                            <button className="vf-clear-x" onClick={() => { setFormVisible(false); resetForm(); }} title="Cerrar">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="product-section-content">
                            <form onSubmit={guardarGasto} className="gasto-form-grid">
                                <label className="gasto-field gasto-field-wide">
                                    <span>Concepto *</span>
                                    <input
                                        type="text"
                                        value={concepto}
                                        onChange={e => setConcepto(e.target.value)}
                                        placeholder="Ej. Arriendo local julio"
                                        maxLength={160}
                                        autoFocus
                                    />
                                </label>
                                <label className="gasto-field">
                                    <span>Monto *</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={monto}
                                        onChange={e => setMonto(e.target.value)}
                                        placeholder="0"
                                    />
                                </label>
                                <label className="gasto-field">
                                    <span>Categoría</span>
                                    <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                                        {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </label>
                                <label className="gasto-field">
                                    <span>Método de pago</span>
                                    <select value={metodo} onChange={e => setMetodo(e.target.value)}>
                                        <option value="Efectivo">Efectivo</option>
                                        <option value="Transferencia">Transferencia</option>
                                        <option value="Tarjeta">Tarjeta</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </label>
                                <label className="gasto-field">
                                    <span>Fecha</span>
                                    <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
                                </label>
                                <label className="gasto-field gasto-field-wide">
                                    <span>Nota (opcional)</span>
                                    <input
                                        type="text"
                                        value={nota}
                                        onChange={e => setNota(e.target.value)}
                                        placeholder="Detalle adicional"
                                        maxLength={200}
                                    />
                                </label>
                                <div className="gasto-form-actions">
                                    <button type="button" className="btn-product-secondary" onClick={() => { setFormVisible(false); resetForm(); }}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-product-primary flex-center" disabled={guardando}>
                                        {guardando ? <FontAwesomeIcon icon={faSync} spin /> : <FontAwesomeIcon icon={faPlus} />}
                                        {guardando ? ' Guardando...' : ' Guardar gasto'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabla (desktop) */}
            <div className="global-table-container desktop-only">
                {loading ? (
                    <div className="global-loading-state">
                        <FontAwesomeIcon icon={faSync} size="2x" spin />
                        <div>Cargando gastos...</div>
                    </div>
                ) : filtrados.length > 0 ? (
                    <table className="global-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Concepto</th>
                                <th>Categoría</th>
                                <th>Método</th>
                                <th>Nota</th>
                                <th>Monto</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtrados.map((g, index) => (
                                <tr key={g.idGasto} style={{ animationDelay: `${index * 0.03}s` }}>
                                    <td>{g.fecha}</td>
                                    <td><strong>{g.concepto}</strong></td>
                                    <td>
                                        <span className="global-indicator gasto-cat-badge">
                                            <FontAwesomeIcon icon={faTags} /> {g.categoria}
                                        </span>
                                    </td>
                                    <td>{g.metodo || '—'}</td>
                                    <td className="text-muted">{g.nota || '—'}</td>
                                    <td style={{ fontWeight: 700, color: '#dc2626' }}>{formatMoney(g.monto)}</td>
                                    <td>
                                        <div className="global-table-actions">
                                            <button className="global-btn-delete" title="Eliminar" onClick={() => eliminarGasto(g)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="global-empty-state">
                        <FontAwesomeIcon icon={faReceipt} size="3x" />
                        <div className="global-empty-title">No hay gastos</div>
                        <div className="global-empty-description">Registra un nuevo gasto o ajusta los filtros.</div>
                    </div>
                )}
            </div>

            {/* Tarjetas (mobile) */}
            {!loading && (
                <div className="mobile-only card-list">
                    {filtrados.length === 0 ? (
                        <div className="global-empty-state">
                            <FontAwesomeIcon icon={faReceipt} size="3x" />
                            <div className="global-empty-title">No hay gastos</div>
                            <div className="global-empty-description">Registra un nuevo gasto o ajusta los filtros.</div>
                        </div>
                    ) : (
                        filtrados.map(g => (
                            <div className="product-card" key={g.idGasto}>
                                <div className="card-header">
                                    <div className="card-image-container">
                                        <div className="card-image-placeholder">
                                            <FontAwesomeIcon icon={faReceipt} />
                                        </div>
                                    </div>
                                    <div className="card-info">
                                        <div className="card-title">{g.concepto}</div>
                                        <div>
                                            <span className="global-indicator gasto-cat-badge">
                                                <FontAwesomeIcon icon={faTags} /> {g.categoria}
                                            </span>
                                        </div>
                                        <div className="text-muted">{g.fecha} · {g.metodo || 'Sin método'}</div>
                                        {g.nota && <div className="text-muted">{g.nota}</div>}
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <div className="card-stock">
                                        <span className="stock-label">Monto:</span>
                                        <span className="stock-value" style={{ color: '#dc2626' }}>{formatMoney(g.monto)}</span>
                                    </div>
                                    <div className="card-actions">
                                        <button className="global-btn-delete" title="Eliminar" onClick={() => eliminarGasto(g)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
